import React, { useEffect, useMemo, useRef, useState } from "react";

import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  List,
  SxProps,
  TextField,
  Theme
} from "@mui/material";
import { Instance as PopperInstance } from "@popperjs/core";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useInvestorsSelector } from "store";
import { IInvestor } from "store/types/deal/investor";

export interface InvestorsGroupValue {
  investorId: number | null;
  groups: string[];
}

interface InvestorsGroupAutocompleteProps {
  label?: string;
  sx?: SxProps<Theme>;

  value: InvestorsGroupValue;
  onChange: (value: InvestorsGroupValue) => void;

  onCreateInvestor: () => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  dealInvestorIds: number[];
  allInvestorsCommitmentOption: boolean;
}

type GroupOption = {
  groupName: string;
  investors: IInvestor[];
};

export const InvestorsGroupAutocomplete: React.FC<InvestorsGroupAutocompleteProps> = ({
  label = "Investor",
  sx,
  value,
  onChange,
  onCreateInvestor,
  error,
  helperText,
  disabled,
  allInvestorsCommitmentOption,
  dealInvestorIds
}) => {
  const { investorId: selectedInvestorId, groups: selectedGroups } = value;
  const investors = useInvestorsSelector();

  const popperRef = useRef<PopperInstance | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLUListElement | null>(null);
  const scrollPositionRef = useRef<number>(0);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Restore scroll position after expandedGroups or selectedInvestorId changes
  useEffect(() => {
    if (listboxRef.current) {
      // Use double requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (listboxRef.current) {
            listboxRef.current.scrollTop = scrollPositionRef.current;
          }
        });
      });
    }
  }, [expandedGroups, selectedInvestorId]);

  const handleToggleGroup = (groupName: string) => {
    // Save scroll position before state update
    if (listboxRef.current) {
      scrollPositionRef.current = listboxRef.current.scrollTop;
    }

    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });

    // Restore scroll position after render using double requestAnimationFrame for better timing
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (listboxRef.current) {
          listboxRef.current.scrollTop = scrollPositionRef.current;
        }
        popperRef.current?.update();
      });
    });
  };

  const groupOptions = useMemo((): GroupOption[] => {
    const dealInvestorIdsSet = new Set(dealInvestorIds);
    const groupsMap = new Map<string, IInvestor[]>();
    const investorsWithoutGroup: IInvestor[] = [];
    const accountInvestors: IInvestor[] = [];

    investors.forEach((investor) => {
      const isDealInvestor = dealInvestorIdsSet.has(investor.id);

      if (isDealInvestor) {
        // Deal investors stay in their groups
        if (investor.group) {
          const groupName = investor.group;
          if (!groupsMap.has(groupName)) {
            groupsMap.set(groupName, []);
          }
          groupsMap.get(groupName)!.push(investor);
        } else {
          investorsWithoutGroup.push(investor);
        }
      } else {
        // All other investors go to "Account Investors" group
        accountInvestors.push(investor);
      }
    });

    const result = Array.from(groupsMap.entries())
      .map(([groupName, investors]) => ({
        groupName,
        investors
      }))
      .sort((a, b) => a.groupName.localeCompare(b.groupName));

    // Add "No Group" option for deal investors without a group
    if (investorsWithoutGroup.length) {
      result.push({
        groupName: "No Group",
        investors: investorsWithoutGroup
      });
    }

    // Add "Account Investors" group for all non-deal investors
    if (accountInvestors.length) {
      result.push({
        groupName: "Account Investors",
        investors: accountInvestors
      });
    }

    return result;
  }, [investors, dealInvestorIds]);

  const selectedGroupOptions = useMemo((): GroupOption[] => {
    return groupOptions.filter((opt) => selectedGroups.includes(opt.groupName));
  }, [groupOptions, selectedGroups]);

  const selectedInvestor = useMemo(() => {
    if (selectedInvestorId === null) return null;
    return investors.find((inv) => inv.id === selectedInvestorId) || null;
  }, [investors, selectedInvestorId]);

  const handleGroupChange = (_: React.SyntheticEvent, newValue: GroupOption[]) => {
    const newGroups = newValue.map((opt) => opt.groupName);
    // Clear investor if selecting groups
    onChange({
      investorId: newGroups.length > 0 ? null : selectedInvestorId,
      groups: newGroups
    });
  };

  const handleGroupCheckboxToggle = (groupName: string) => {
    // Save scroll position before state update
    if (listboxRef.current) {
      scrollPositionRef.current = listboxRef.current.scrollTop;
    }

    const isSelected = selectedGroups.includes(groupName);
    const newGroups = isSelected ? selectedGroups.filter((g) => g !== groupName) : [...selectedGroups, groupName];

    onChange({
      investorId: !isSelected ? null : selectedInvestorId,
      groups: newGroups
    });

    // Restore scroll position after render using double requestAnimationFrame for better timing
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (listboxRef.current) {
          listboxRef.current.scrollTop = scrollPositionRef.current;
        }
        popperRef.current?.update();
      });
    });
  };

  const handleInvestorClick = (investor: IInvestor) => {
    // Save scroll position before state update
    if (listboxRef.current) {
      scrollPositionRef.current = listboxRef.current.scrollTop;
    }

    const isSelected = selectedInvestorId === investor.id;
    onChange({
      investorId: isSelected ? null : investor.id,
      groups: isSelected ? selectedGroups : []
    });

    // Restore scroll position after render using double requestAnimationFrame for better timing
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (listboxRef.current) {
          listboxRef.current.scrollTop = scrollPositionRef.current;
        }
        popperRef.current?.update();
      });
    });
  };

  return (
    <Autocomplete<GroupOption, true, false, false>
      disabled={disabled}
      sx={sx}
      multiple
      disableCloseOnSelect
      options={groupOptions}
      value={selectedGroupOptions}
      onChange={handleGroupChange}
      getOptionLabel={(option) => option.groupName}
      isOptionEqualToValue={(option, value) => option.groupName === value.groupName}
      renderTags={(value, getTagProps) => {
        return (
          <div
            style={{
              maxHeight: "85px",
              overflowY: "auto",
              overflowX: "hidden",
              display: "flex",
              flexWrap: "wrap",
              gap: "4px"
            }}
          >
            {selectedInvestor && (
              <MaterialTooltip title={selectedInvestor.name} isShow>
                <Chip
                  label={selectedInvestor.name}
                  size="small"
                  onDelete={() => onChange({ investorId: null, groups: selectedGroups })}
                  sx={{ maxWidth: 150 }}
                />
              </MaterialTooltip>
            )}
            {value.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <MaterialTooltip key={key} title={option.groupName} isShow>
                  <Chip label={option.groupName} size="small" {...tagProps} sx={{ maxWidth: 150 }} />
                </MaterialTooltip>
              );
            })}
          </div>
        );
      }}
      renderInput={(params) => {
        const { InputProps, ...other } = params;
        const hasSelectedGroups = selectedGroupOptions.length;

        return (
          <TextField
            inputRef={inputRef}
            {...other}
            InputProps={{
              ...InputProps,
              startAdornment: (
                <>
                  {selectedInvestorId === -1 && !hasSelectedGroups && (
                    <MaterialTooltip title="All investors - Split by commitment" isShow>
                      <Chip
                        label="All investors - Split by commitment"
                        size="small"
                        onDelete={() => onChange({ investorId: null, groups: selectedGroups })}
                        sx={{ maxWidth: 150, marginRight: 0.5 }}
                      />
                    </MaterialTooltip>
                  )}
                  {selectedInvestor && !hasSelectedGroups && (
                    <MaterialTooltip title={selectedInvestor.name} isShow>
                      <Chip
                        label={selectedInvestor.name}
                        size="small"
                        onDelete={() => onChange({ investorId: null, groups: selectedGroups })}
                        sx={{ maxWidth: 150, marginRight: 0.5 }}
                      />
                    </MaterialTooltip>
                  )}
                  {InputProps?.startAdornment}
                </>
              )
            }}
            label={label}
            placeholder="Select"
            error={!!error}
            helperText={helperText}
          />
        );
      }}
      slotProps={{
        popper: {
          popperRef: popperRef,
          disablePortal: true
        }
      }}
      renderOption={(optionProps, option) => {
        const { key, ...rest } = optionProps;
        const isSelected = selectedGroups.includes(option.groupName);

        return (
          <li key={key} style={{ padding: 0 }}>
            <GroupWithCheckbox
              groupOption={option}
              isSelected={isSelected}
              popperRef={popperRef}
              searchValue={inputRef.current?.value || ""}
              expandedGroups={expandedGroups}
              onToggleGroup={handleToggleGroup}
              onCheckboxToggle={handleGroupCheckboxToggle}
              selectedInvestorId={selectedInvestorId}
              onInvestorClick={handleInvestorClick}
              optionProps={rest}
            />
          </li>
        );
      }}
      ListboxProps={{
        ref: listboxRef,
        sx: {
          "& .MuiAutocomplete-option": {
            padding: 0
          }
        }
      }}
      PaperComponent={({ children, ...paperProps }) => (
        <Box
          {...paperProps}
          sx={{
            "& .MuiAutocomplete-listbox": {
              maxHeight: "200px"
            },
            backgroundColor: "white",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
            borderRadius: "4px"
          }}
        >
          {allInvestorsCommitmentOption && (
            <Button
              variant="text"
              fullWidth
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={() => {
                onChange({ investorId: -1, groups: [] });
              }}
              sx={{
                backgroundColor: selectedInvestorId === -1 ? "rgba(25, 118, 210, 0.08)" : "transparent",
                height: "45px",
                justifyContent: "flex-start",
                padding: "12px 16px",
                color: "#0a3142",
                fontWeight: 500,
                borderTop: "1px solid rgba(0, 0, 0, 0.12)",
                borderRadius: 0,
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)"
                }
              }}
            >
              All investors - Split by commitment
            </Button>
          )}
          {children}
          <Button
            variant="text"
            fullWidth
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={onCreateInvestor}
            sx={{
              height: "45px",
              justifyContent: "flex-start",
              padding: "12px 16px",
              color: "#0a3142",
              fontWeight: 500,
              borderTop: "1px solid rgba(0, 0, 0, 0.12)",
              borderRadius: 0,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)"
              }
            }}
          >
            Add investor
          </Button>
        </Box>
      )}
    />
  );
};

interface GroupWithCheckboxProps {
  groupOption: GroupOption;
  isSelected: boolean;
  popperRef: React.RefObject<PopperInstance | null>;
  searchValue: string;
  expandedGroups: Set<string>;
  onToggleGroup: (groupName: string) => void;
  onCheckboxToggle: (groupName: string) => void;
  selectedInvestorId: number | null;
  onInvestorClick: (investor: IInvestor) => void;
  optionProps: Omit<React.HTMLAttributes<HTMLLIElement>, "key">;
}

const GroupWithCheckbox: React.FC<GroupWithCheckboxProps> = ({
  groupOption,
  isSelected,
  popperRef,
  searchValue,
  expandedGroups,
  onToggleGroup,
  onCheckboxToggle,
  selectedInvestorId,
  onInvestorClick
}) => {
  const { groupName, investors } = groupOption;
  const isExpanded = expandedGroups.has(groupName);
  const isNoGroup = !investors[0]?.group;

  useEffect(() => {
    if (searchValue.length >= 2 && !isExpanded) {
      onToggleGroup(groupName);
    }
  }, [searchValue]);

  return (
    <Accordion
      expanded={isExpanded}
      sx={{ borderRadius: "0 !important", color: "#0a3142", boxShadow: "none", width: "100%" }}
      onChange={(_, expanded) => {
        onToggleGroup(groupName);
        if (expanded) {
          // Restore scroll position after accordion expands
          requestAnimationFrame(() => {
            setTimeout(() => {
              popperRef.current?.update();
            }, 300);
          });
        }
      }}
    >
      <AccordionSummary
        expandIcon={<ArrowForwardIosIcon sx={{ fontSize: "0.9rem", fill: "#5D7782", transform: "rotate(90deg)" }} />}
        sx={{
          fontWeight: 600,
          fontSize: 15,
          "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
            transform: "rotate(180deg)"
          },
          "& .MuiAccordionSummary-content, .MuiAccordionSummary-content.Mui-expanded": {
            margin: 0,
            overflow: "hidden"
          },
          padding: "6px 12px !important",
          minHeight: "28px !important",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04) !important"
          }
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", width: "100%", overflow: "hidden", height: "30px" }}>
          {!isNoGroup && groupName !== "Account Investors" && (
            <Checkbox
              checked={isSelected}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                onCheckboxToggle(groupName);
              }}
              sx={{ padding: "4px", marginRight: "8px", flexShrink: 0 }}
            />
          )}
          <MaterialTooltip title={groupName}>
            <Box
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: "14px",
                fontWeight: 500
              }}
            >
              {groupName}
            </Box>
          </MaterialTooltip>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: "0px" }}>
        <List sx={{ padding: 0 }}>
          {groupOption.investors.map((investor: IInvestor) => {
            const isInvestorSelected = selectedInvestorId === investor.id;
            return (
              <li
                key={investor.id}
                onClick={(e) => {
                  e.stopPropagation();
                  onInvestorClick(investor);
                }}
                style={{
                  fontSize: 14,
                  margin: 0,
                  padding: "6px 12px 6px 24px",
                  cursor: "pointer",
                  backgroundColor: isInvestorSelected ? "rgba(25, 118, 210, 0.08)" : "transparent"
                }}
                onMouseEnter={(e) => {
                  if (!isInvestorSelected) {
                    e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.04)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isInvestorSelected
                    ? "rgba(25, 118, 210, 0.08)"
                    : "transparent";
                }}
              >
                {investor.name}
              </li>
            );
          })}
        </List>
      </AccordionDetails>
    </Accordion>
  );
};
